import { FormEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Camera, Mic, Upload } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import { ContentBlocksPreview } from "./ContentBlocksPreview";
import { AudioRecorder } from "./AudioRecorder";
import { AudioPreview, MultimodalPreview } from "./MultimodalPreview";
import { ImageDescriptionDialog } from "./ImageDescriptionDialog";
import { NoImagesWarningDialog } from "./NoImagesWarningDialog";
import { ChatInputActionBar } from "./ChatInputActionBar";
import type { StorageAudioBlock } from "../types/messages";
import { useStreamContext } from "@/providers/Stream";

type ChatInputMode = "normal" | "capture";

export interface ChatInputProps {
  // Input mode
  mode?: ChatInputMode;

  // Input state
  input: string;
  setInput: (value: string) => void;

  // File upload
  contentBlocks: any[];
  handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  dropRef: React.RefObject<HTMLDivElement | null>;
  removeBlock: (index: number) => void;
  updateBlockDescription?: (index: number, description: string, descriptionAudioUrls?: string[]) => void;
  dragOver: boolean;
  handlePaste: (
    e: React.ClipboardEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => void;

  // Form submission
  handleSubmit: (e: FormEvent) => void;
  canSubmit: boolean;
  isLoading: boolean;

  // Stream control
  onStop: () => void;

  // Scroll state (for gradient effect)
  isAtBottom?: boolean;

  // Audio recording
  isRecording?: boolean;
  recordingDuration?: number;
  analyserNode?: AnalyserNode | null;
  onStartRecording?: () => void;
  onStopRecording?: () => void;
  onCancelRecording?: () => void;
  isUploadingAudio?: boolean;

  // Mobile document panel toggle
  onOpenDocument?: () => void;

  // Capture mode specific
  onCaptureComplete?: () => void;
  onCaptureCancel?: () => void;
  conversationName?: string;

  // Capture mode validation helpers
  hasAudioRecordings?: boolean;
  hasImages?: boolean;
}

/**
 * Chat input form with multimodal support (text + file upload).
 *
 * Features:
 * - Auto-growing textarea with field-sizing-content
 * - File upload via drag-drop or file picker
 * - Content blocks preview
 * - Thinking toggle switch
 * - Submit/stop button based on loading state
 * - Enter to submit, Shift+Enter for newline
 * - Privacy notice footer
 *
 * @param props - Form configuration
 */
export function ChatInput({
  mode = "normal",
  input,
  setInput,
  contentBlocks,
  handleFileUpload,
  dropRef,
  removeBlock,
  updateBlockDescription,
  dragOver,
  handlePaste,
  handleSubmit,
  canSubmit,
  isLoading,
  onStop,
  isAtBottom = true,
  isRecording = false,
  recordingDuration = 0,
  analyserNode = null,
  onStartRecording,
  onStopRecording,
  onCancelRecording,
  isUploadingAudio = false,
  onOpenDocument,
  // Capture mode props
  onCaptureComplete,
  onCaptureCancel,
  conversationName,
  // Capture mode validation helpers
  hasAudioRecordings = false,
  hasImages = false,
}: ChatInputProps) {
  const { t } = useTranslation("chat");
  const { t: tCommon } = useTranslation("common");
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [showNoImagesWarning, setShowNoImagesWarning] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check if agent is currently running
  const { agentStatus } = useStreamContext();
  const isAgentBusy = agentStatus === 'RUNNING_AGENT_LOOP';

  // Filter audio items from content blocks for capture mode
  const audioBlocks = contentBlocks.filter(
    (block): block is StorageAudioBlock =>
      block.type === "audio" && block.source_type === "storage"
  );

  // Filter image items from content blocks for capture mode
  const imageBlocks = contentBlocks.filter((block) => block.type !== "audio");

  // Handle photo button click in capture mode
  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  // Handle file change in capture mode
  const handleCaptureFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileUpload(e);
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle "Rapport opstellen" button click in capture mode
  // Shows warning if no images are present
  const handleCaptureCompleteClick = () => {
    if (!hasImages) {
      setShowNoImagesWarning(true);
    } else {
      onCaptureComplete?.();
    }
  };

  // ============================================
  // CAPTURE MODE - Full-width buttons for recording/photos
  // ============================================
  if (mode === "capture") {
    return (
      <div
        ref={dropRef}
        className={cn(
          "fixed inset-0 z-50 flex flex-col bg-background",
          dragOver && "ring-4 ring-inset ring-primary/50"
        )}
      >
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-border bg-utrecht-blue p-4">
          <button
            onClick={onCaptureCancel}
            className="p-1 text-white hover:bg-white/10 rounded transition-colors"
            aria-label={tCommon("capture.actions.cancel")}
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold text-white truncate">
              {conversationName}
            </h1>
            <p className="text-sm text-white/80">
              {tCommon("capture.subtitle")}
            </p>
          </div>
        </div>

        {/* Drag & drop overlay */}
        {dragOver && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm pointer-events-none">
            <div className="flex flex-col items-center gap-4 p-8 border-2 border-dashed border-primary rounded-lg bg-background">
              <Upload className="h-12 w-12 text-primary" />
              <p className="text-lg font-medium text-primary">
                {tCommon("capture.dropzone")}
              </p>
            </div>
          </div>
        )}

        {/* Main content */}
        <div className="flex-1 flex flex-col p-4 overflow-y-auto">
          {/* Audio recordings sectie - ALTIJD tonen */}
          <div className="flex flex-col mb-4">
            <h3 className="text-sm font-medium text-foreground/80 px-0 py-2">
              {tCommon("capture.audio.title")}
              {audioBlocks.length > 0 && ` (${audioBlocks.length})`}
            </h3>
            {/* Bestaande opnames */}
            {audioBlocks.length > 0 && (
              <div className="flex flex-col gap-2">
                {audioBlocks.map((block, index) => {
                  return (
                    <AudioPreview
                      key={block.relative_gcs_path || `audio-${index}`}
                      block={block}
                      removable={!block.uploading}
                      onRemove={() => {
                        const blockIndex = contentBlocks.indexOf(block);
                        if (blockIndex !== -1) {
                          removeBlock(blockIndex);
                        }
                      }}
                      label={audioBlocks.length > 1 ? index + 1 : undefined}
                      variant="full"
                    />
                  );
                })}
              </div>
            )}
            {/* Klikbare placeholder - altijd tonen, triggert opname */}
            {!isRecording && (
              <button
                type="button"
                onClick={onStartRecording}
                disabled={isUploadingAudio || isRecording}
                className={cn(
                  "w-full bg-muted/30 border border-dashed border-muted-foreground/30 px-4 py-4 rounded hover:bg-muted/50 hover:border-muted-foreground/50 transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50",
                  audioBlocks.length > 0 && "mt-2"
                )}
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-muted-foreground/20">
                    <Mic className="h-4 w-4 text-muted-foreground/50" />
                  </div>
                  <div className="relative flex-1 h-2 bg-muted-foreground/10 rounded-full" />
                  <span className="text-xs text-muted-foreground/50 whitespace-nowrap font-mono min-w-[70px] text-right">
                    0:00 / 0:00
                  </span>
                </div>
              </button>
            )}
          </div>

          {/* Inline recording indicator - shows while recording */}
          {isRecording && onStopRecording && onCancelRecording && (
            <div className="mb-4">
              <AudioRecorder
                isRecording={isRecording}
                duration={recordingDuration}
                analyserNode={analyserNode}
                onStop={onStopRecording}
                onCancel={onCancelRecording}
              />
            </div>
          )}

          {/* Afbeeldingen sectie - ALTIJD tonen */}
          <div className="flex flex-col mb-4">
            <h3 className="text-sm font-medium text-foreground/80 px-0 py-2">
              {tCommon("capture.images.title")}
              {imageBlocks.length > 0 && ` (${imageBlocks.length})`}
            </h3>
            {/* Afbeeldingen en placeholder in één flex container */}
            <div className="flex flex-wrap gap-2">
              {/* Bestaande afbeeldingen */}
              {imageBlocks.map((block) => {
                const actualIndex = contentBlocks.indexOf(block);
                return (
                  <MultimodalPreview
                    key={`image-${actualIndex}`}
                    block={block}
                    removable
                    onRemove={() => {
                      if (actualIndex !== -1) {
                        removeBlock(actualIndex);
                      }
                    }}
                    onImageClick={updateBlockDescription ? () => {
                      setSelectedImageIndex(actualIndex);
                    } : undefined}
                    size="md"
                  />
                );
              })}
              {/* Klikbare placeholder - naast afbeeldingen */}
              <button
                type="button"
                onClick={handlePhotoClick}
                disabled={isUploadingAudio || isRecording}
                className="h-24 w-24 bg-muted/30 border border-dashed border-muted-foreground/30 rounded flex items-center justify-center hover:bg-muted/50 hover:border-muted-foreground/50 transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Camera className="h-8 w-8 text-muted-foreground/30" />
              </button>
            </div>
          </div>

          {/* Action buttons - disabled during recording */}
          <div className="flex flex-col gap-3 mt-auto">
            <Button
              type="button"
              size="xxl"
              onClick={onStartRecording}
              disabled={isUploadingAudio || isRecording}
              className="w-full rounded-none bg-utrecht-red hover:bg-utrecht-red/90 text-white flex items-center justify-center gap-3"
            >
              <Mic className="size-7" strokeWidth={2} />
              <span className="text-lg font-medium">
                {audioBlocks.length > 0
                  ? tCommon("capture.buttons.newRecording")
                  : tCommon("capture.buttons.audio")}
              </span>
            </Button>

            <Button
              type="button"
              size="xxl"
              onClick={handlePhotoClick}
              disabled={isUploadingAudio || isRecording}
              className="w-full rounded-none bg-utrecht-gold hover:bg-utrecht-gold/90 text-white flex items-center justify-center gap-3"
            >
              <Camera className="size-7" strokeWidth={2} />
              <span className="text-lg font-medium">{tCommon("capture.buttons.photo")}</span>
            </Button>
          </div>

          {/* Hidden file input for camera/photos */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            onChange={handleCaptureFileChange}
            className="hidden"
          />
        </div>

        {/* Footer with action buttons */}
        <div className="border-t border-border p-4 space-y-3 bg-background">
          <Button
            size="xxl"
            onClick={handleCaptureCompleteClick}
            disabled={!canSubmit}
            className="w-full bg-utrecht-blue hover:bg-utrecht-blue/90 text-white"
          >
            {!hasAudioRecordings ? (
              tCommon("capture.actions.requiresAudio")
            ) : !canSubmit ? (
              <span className="flex items-center justify-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                {tCommon("capture.actions.processing")}
              </span>
            ) : (
              tCommon("capture.actions.done")
            )}
          </Button>
        </div>

        {/* Image description dialog */}
        <ImageDescriptionDialog
          open={selectedImageIndex !== null}
          onOpenChange={(open) => {
            if (!open) setSelectedImageIndex(null);
          }}
          block={selectedImageIndex !== null ? contentBlocks[selectedImageIndex] : null}
          onSave={(description, descriptionAudioUrls) => {
            if (selectedImageIndex !== null && updateBlockDescription) {
              updateBlockDescription(selectedImageIndex, description, descriptionAudioUrls);
            }
            setSelectedImageIndex(null);
          }}
        />

        {/* No images warning dialog */}
        <NoImagesWarningDialog
          open={showNoImagesWarning}
          onOpenChange={setShowNoImagesWarning}
          onConfirm={() => onCaptureComplete?.()}
          onCancel={() => {}}
        />
      </div>
    );
  }

  // ============================================
  // NORMAL MODE - Standard chat input
  // ============================================
  return (
    <div className="bg-background sticky bottom-0 z-30 flex flex-col">
      {/* Fade gradient overlay when scrolled up - positioned below scroll button */}
      <div
        className={cn(
          "pointer-events-none absolute -top-6 right-0 left-0 z-[5] h-6 transition-opacity duration-300",
          "via-background/60 to-background bg-gradient-to-b from-transparent",
          isAtBottom ? "opacity-0" : "opacity-100",
        )}
      />
      <div className="group relative">
        {/* Existing input wrapper */}
        <div
          ref={dropRef}
          className={cn(
            "relative z-10 mx-auto w-full shadow-xs bg-utrecht-light-blue",
            dragOver && "border-2 border-primary border-dotted",
          )}
        >
        <form
          onSubmit={handleSubmit}
          className="relative mx-auto grid grid-rows-[1fr_auto]"
        >
          {/* Audio recording - part of the input */}
          <AnimatePresence>
            {isRecording && onStopRecording && onCancelRecording && (
              <AudioRecorder
                isRecording={isRecording}
                duration={recordingDuration}
                analyserNode={analyserNode}
                onStop={onStopRecording}
                onCancel={onCancelRecording}
                className="border-t border-utrecht-blue/20"
              />
            )}
          </AnimatePresence>

          <ContentBlocksPreview
            blocks={contentBlocks}
            onRemove={removeBlock}
            onUpdateDescription={updateBlockDescription}
            className={cn(
              "border-t border-utrecht-blue/20",
              // Geen border-t als audio actief is (die heeft al border-t)
              isRecording && "border-t-0"
            )}
          />

          <div className="flex border-t border-utrecht-blue/20">
            {/* Scrollable textarea container */}
            <div className="flex-1 min-h-[108px] max-h-[50vh] overflow-y-auto flex">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onPaste={handlePaste}
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" &&
                    !e.shiftKey &&
                    !e.metaKey &&
                    !e.nativeEvent.isComposing
                  ) {
                    e.preventDefault();
                    const el = e.target as HTMLElement | undefined;
                    const form = el?.closest("form");
                    form?.requestSubmit();
                  }
                }}
                placeholder={isAgentBusy ? t("agentBusyPlaceholder") : t("placeholder")}
                maxLength={400}
                rows={3}
                disabled={isAgentBusy}
                className={cn(
                  "w-full flex-1 field-sizing-content resize-none border-none bg-transparent p-4 shadow-none ring-0 outline-none focus:ring-0 focus:outline-none min-h-[108px]",
                  isAgentBusy && "cursor-not-allowed opacity-50"
                )}
              />
            </div>

            {/* Vertical action bar on the right */}
            <ChatInputActionBar
              onOpenDocument={onOpenDocument}
              isRecording={isRecording}
              onStartRecording={onStartRecording}
              onStopRecording={onStopRecording}
              handleFileUpload={handleFileUpload}
              fileInputId="file-input"
              isLoading={isLoading}
              canSubmit={canSubmit}
              onStop={onStop}
              isAgentBusy={isAgentBusy}
              submitIcon="arrow-up"
            />
          </div>
        </form>
        </div>
      </div>

      {/* Privacy message footer */}
      {/* <div className="text-muted-foreground py-2 text-center text-sm">
        {t("privacyNotice")}
      </div> */}
    </div>
  );
}
